import { gql } from '@apollo/client';
import { STATEMENT_PREVIEW_FIELDS } from '../queries/statement';

/**
 * Read a client's Consolidated Account Statement — parse only, book nothing.
 *
 * The file travels as a multipart upload (apollo-upload-client, already the
 * app's HTTP link). `password` is the first holder's PAN for an encrypted CAS;
 * the backend tries the client's PAN on file before asking, so it is only
 * needed when that is absent or the statement is somebody else's.
 *
 * A statement that cannot be opened comes back as `success: false` with a
 * message rather than as a GraphQL error, and with no preview. The message is
 * the same whether no password was supplied or the wrong one was, so the screen
 * tracks which of the two it is.
 *
 * Nothing is booked here. The preview names the investor it resolved the
 * statement to and the portfolio that WOULD be written, which schemes matched a
 * security and which did not, and what would change against an existing
 * portfolio — all of which the advisor has to see before
 * `applyPortfolioStatement` writes anything.
 *
 * The portfolio's NAME is the backend's to choose: it derives one from the
 * statement and the target it resolved, so there is nothing to send.
 */
export const UPLOAD_PORTFOLIO_STATEMENT = gql`
  mutation UploadPortfolioStatement($file: Upload!, $password: String) {
    uploadPortfolioStatement(file: $file, password: $password) {
      success
      message
      preview {
        ${STATEMENT_PREVIEW_FIELDS}
      }
    }
  }
`;

/**
 * Book a parsed statement into the investor's portfolio.
 *
 * Reads the rows stored by the upload, so it needs no file and no password.
 *
 * `overrides` resolve the schemes the matcher could not settle: an AMBIGUOUS
 * scheme names which candidate security it is, and any scheme can be skipped
 * outright. A scheme left AMBIGUOUS or UNMATCHED is not booked.
 *
 * `investorEmail` and `investorPhone` are how an investor the platform does not
 * yet hold becomes one: the statement carries both, the preview shows what it
 * read, and these are what is actually used — so a typo in the document can be
 * corrected before an account is created against it. `clientCreated` in the
 * response says whether that happened.
 *
 * `goalType` is the kind of goal the portfolio is created under, and what it is
 * called; `CapitalTarget` when nothing is sent. (`goalId` also exists on the
 * resolver, for attaching to an existing goal, and is deliberately unused.)
 *
 * `startDate` is the day a holdings sheet's funds are booked as bought — the
 * same date the preview was last asked with. `SHEET` uploads only; the backend
 * refuses it for any other format.
 *
 * A scheme whose parse failed its own arithmetic (UNIT_RECONCILIATION,
 * CLOSING_MISSING, NO_FOLIOS) is held back on its own and reported in
 * `schemesSkipped`; the rest still book. The whole call only fails when every
 * scheme ends up skipped.
 *
 * `syntheticRows` are opening positions the ledger implies but never printed —
 * a folio whose history starts before the statement period. They are re-derived
 * on every apply rather than matched, which is why a re-import reads them as
 * SYNTHETIC again while the real rows read ALREADY_BOOKED.
 */
export const APPLY_PORTFOLIO_STATEMENT = gql`
  mutation ApplyPortfolioStatement(
    $uploadId: Int!
    $goalType: String
    $investorEmail: String
    $investorPhone: String
    $overrides: [SchemeMatchOverrideInput!]
    $startDate: Date
  ) {
    applyPortfolioStatement(
      uploadId: $uploadId
      goalType: $goalType
      investorEmail: $investorEmail
      investorPhone: $investorPhone
      overrides: $overrides
      startDate: $startDate
    ) {
      success
      message
      securitySymbol
      portfolioName
      portfolioCreated
      asOfDate
      clientUserId
      clientCreated
      investorStatus
      goalId
      schemesBooked
      schemesSkipped
      transactionsNew
      transactionsExisting
      syntheticRows
      reconciliation
      holdingsWritten
      returnsComputed
      metricsQueued
    }
  }
`;
